<template>
  <HeadTitle
    title="Cartas y constancias"
    description="Lista de solicitudes de cartas y constancias"
  />
  <divider></divider>
  <Toolbar class="mb-4 md:w-11">
    <template #start> </template>
    <template #end>
      <Button
        label="Nueva solicitud"
        icon="pi pi-plus"
        severity="primary"
        class="mr-2 text-sm font-light"
        @click="newLetterDialog = !newLetterDialog"
      />
    </template>
  </Toolbar>
  <DataTable
    :value="letterRequests.data"
    dataKey="id"
    tableStyle="min-width: 60rem"
    class="tabla mb-3"
    paginator
    lazy
    :rows="letterRequests.meta.perPage"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    :first="first"
    scrollable
    :total-records="letterRequests.meta.total"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    :currentPageReportTemplate="currentPageReportTemplate"
    @page="onPaginate"
  >
    <Column field="letterCertifConfig.name" header="Nombre"></Column>
    <Column
      field="letterCertifConfig.description"
      header="Descripción"
    ></Column>
    <Column
      field="letterCertifConfig.generationType"
      header="Tipo de generación"
    >
      <template #body="{ data, field }">
        {{ formatGenerationType(data.letterCertifConfig?.generationType) }}
      </template>
    </Column>
    <Column
      field="status"
      header="Estado"
      :showFilterMenu="false"
      :filterMenuStyle="{ width: '3rem' }"
    >
      <template #body="{ data, field }">
        <Tag
          :value="formatRequestState(data[field])"
          :severity="getStatusLabel(data[field])"
          class="font-medium"
        ></Tag>
      </template>
    </Column>
    <Column field="updatedAt" header="Última actualización">
      <template #body="{ data }">
        {{ formatDateWithHour(data.createdAt) }}
      </template>
    </Column>
    <Column field="resolutionDate" header="Fecha de resolución">
      <template #body="{ data, field }">
        <span v-if="data[field]">
          {{ formatDateWithHour(data.resolutionDate) }}</span
        >
        <span v-else>{{ 'No definida' }}</span>
      </template>
    </Column>
    <Column class="actions" header="Archivo">
      <template #body="{ data }">
        <Button
          :icon="getIcon(data.status)"
          severity="secondary"
          :disabled="isFileButtonAvailable(data.status)"
          class="action-btn"
          @click="downloadLetter(data.urlFile)"
        />
      </template>
    </Column>
  </DataTable>
  <Dialog
    v-model:visible="newLetterDialog"
    :style="{ width: '450px' }"
    header="Nueva solicitud de carta"
    :modal="true"
    class="p-fluid"
  >
    <div class="p-field">
      <div class="p-field">
        <label for="letter" class="font-medium"
          >Carta y certificados disponibles</label
        >
        <Dropdown
          v-model="selectedLetter"
          :options="availableLetters"
          optionLabel="name"
          placeholder="Seleccione una carta"
          class="mt-3"
          show-clear
          filter
        />
      </div>

      <div class="p-field mt-4">
        <label class="font-bold">Descripción:</label>
        <p class="mt-3 text-left font-normal font-italic capitalize">
          {{ selectedLetter?.description }}
        </p>
      </div>
      <InlineMessage
        severity="info"
        v-if="selectedLetter?.generationType === 'WITH_APPROVAL'"
        class="mt-4"
      >
        <p class="text-sm">
          Se requiere aprobación para la carta o certificado. La solicitud será
          notificada al aprobador correspondiente.
        </p>
      </InlineMessage>
    </div>
    <template #footer>
      <Button label="Cancelar" icon="pi pi-times" text @click="closeDialog" />
      <Button
        label="Solicitar"
        icon="pi pi-check"
        text
        @click="saveRequest"
        :disabled="!isValidToSave"
      />
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
  import { computed, onMounted, reactive, ref, watch } from 'vue'
  import Toolbar from 'primevue/toolbar'
  import { storeToRefs } from 'pinia'
  import { useUserStore } from '@/store'
  import {
    createLetterRequest,
    getLetterRequestsByEmployee,
  } from '../services/LetterRequestService'
  import { getAvailableLettersByEmployee } from '../services/LetterConfgServices'
  import LetterRequest from '../models/LetterRequest'
  import Tag from 'primevue/tag'
  import { formatDateWithHour } from '@/shared/utils/fomartDateWithHours'
  import LetterAvailables from '../models/LettersAvailables'
  import useLoading from '@/store/load'
  import useNotifications from '@/store/notification'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import { useUrlPresigned } from '@/shared/composables/useUrlPresigned'

  const { user } = storeToRefs(useUserStore())
  const newLetterDialog = ref(false)
  const notificationStore = useNotifications()
  const first = ref(0)
  const letterRequests = reactive({
    data: [] as LetterRequest[],
    meta: {
      total: 0,
      lastPage: 0,
      currentPage: 1,
      perPage: 10,
      prev: 0,
      next: 0,
      sortField: undefined,
      sortOrder: undefined,
    },
  })

  const availableLetters = ref<LetterAvailables[]>()
  const loadingStore = useLoading()
  const selectedLetter = ref<LetterAvailables>()

  onMounted(async () => {
    loadingStore.setLoadingState()
    await fetchLetterRequests()
    await fetchAvailablesLetters()
    loadingStore.dismissLoadingState()
  })

  const saveRequest = async () => {
    if (!user.value?.employeeId || !selectedLetter.value) return
    loadingStore.setLoadingState()
    const response = await createLetterRequest({
      avoidNotifications: false,
      employeeId: user.value?.employeeId,
      letterCertifConfigId: selectedLetter.value?.id,
    })

    if (response === 'OK') {
      newLetterDialog.value = false
      new Promise((resolve) => {
        resolve(
          notificationStore.addSuccesNotification({
            detail: 'Solicitud de carta creada exitosamente',
            severity: 'success',
            summary: 'Solicitud de carta',
          })
        )
      }).then(async () => {
        await fetchLetterRequests()
        await fetchAvailablesLetters()
      })
    }
    loadingStore.dismissLoadingState()
  }

  let requestCount = 0
  let debounceTimeout: ReturnType<typeof setTimeout>

  const hookLetterRequestAutomaticPending = () => {
    if (debounceTimeout) clearTimeout(debounceTimeout)
    debounceTimeout = setTimeout(async () => {
      if (requestCount >= 7) return
      const existAutomaticPendingRequests = letterRequests.data?.find(
        (request) =>
          request.status === 'PENDING' &&
          request.letterCertifConfig.generationType === 'AUTOMATIC'
      )
      if (!existAutomaticPendingRequests) return
      requestCount++
      await fetchLetterRequests()
    }, 1000)
  }

  const onPaginate = (event: any) => {
    const { first, rows, page } = event
    letterRequests.meta.perPage = rows
    letterRequests.meta.currentPage = page + 1
    letterRequests.meta.lastPage = Math.floor(
      letterRequests.meta.total / letterRequests.meta.perPage
    )
    fetchLetterRequests()
  }

  const fetchLetterRequests = async () => {
    if (!user.value?.employeeId) return
    loadingStore.setLoadingState()
    const response = await getLetterRequestsByEmployee(user.value?.employeeId, {
      limit: letterRequests.meta.perPage,
      offset: letterRequests.meta.currentPage,
    })
    loadingStore.dismissLoadingState()
    if (!response) return
    letterRequests.data = response.data
    letterRequests.meta.total = response.meta.total
  }

  const fetchAvailablesLetters = async () => {
    if (!user.value?.employeeId) return
    const response = await getAvailableLettersByEmployee(user.value.employeeId)
    if (!response) return
    availableLetters.value = response.data
  }

  const closeDialog = () => {
    newLetterDialog.value = false
    selectedLetter.value = undefined
  }

  const downloadLetter = async (url: string) => {
    const fileUrl = await useUrlPresigned(url)
    window.open(fileUrl, '_blank')
  }

  const isValidToSave = computed(() => {
    return !!selectedLetter.value
  })

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success'
      case 'PENDING':
        return 'warning'
      case 'REJECTED':
        return 'danger'
      case 'GENERATED':
        return 'info'
      case 'ERROR_GENERATING':
        return 'danger'
      default:
        return 'warning'
    }
  }

  const formatRequestState = (state: string) => {
    switch (state) {
      case 'PENDING':
        return 'Pendiente'
      case 'REJECTED':
        return 'Rechazado'
      case 'APPROVED':
        return 'Aprobado'
      case 'GENERATED':
        return 'Generado'
      case 'EXTENAL':
        return 'Externo'
      case 'ERROR_GENERATING':
        return 'Error al generar'
      default:
        return 'Pendiente'
    }
  }

  const formatGenerationType = (generationType: string) => {
    switch (generationType) {
      case 'WITH_APPROVAL':
        return 'Requiere aprobación'
      case 'AUTOMATIC':
        return 'Automática'
      case 'EXTERNAL':
        return 'Externa'
      default:
        return 'Manual'
    }
  }

  const getIcon = (status: string) => {
    return status === 'GENERATED' ? 'pi pi-paperclip' : 'pi pi-ban'
  }

  const isFileButtonAvailable = (status: string): boolean => {
    return (
      status === 'REJECTED' ||
      status === 'PENDING' ||
      status === 'ERROR_GENERATING'
    )
  }

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${letterRequests.meta.total}`
  })
</script>
<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  .actions {
    display: flex;
    width: auto;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  :deep(.p-paginator) {
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    font-size: 14px;
  }
</style>
